import client from "@/lib/api";

const b64ToBytes = (value) => {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const raw = atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const bytesToB64 = (buf) => {
  const raw = String.fromCharCode(...new Uint8Array(buf));
  return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const regOptions = (o) => ({
  ...o,
  challenge: b64ToBytes(o.challenge),
  user: { ...o.user, id: b64ToBytes(o.user.id) },
  excludeCredentials: (o.excludeCredentials ?? []).map((x) => ({ ...x, id: b64ToBytes(x.id) })),
});

const authOptions = (o) => ({
  ...o,
  challenge: b64ToBytes(o.challenge),
  allowCredentials: (o.allowCredentials ?? []).map((x) => ({
    type: x.type || "public-key",
    id: b64ToBytes(x.id),
    ...(x.transports ? { transports: x.transports } : {}),
  })),
});

const regResponse = (c) => ({
  id: c.id, rawId: bytesToB64(c.rawId), type: c.type,
  authenticatorAttachment: c.authenticatorAttachment,
  response: {
    clientDataJSON: bytesToB64(c.response.clientDataJSON),
    attestationObject: bytesToB64(c.response.attestationObject),
    transports: c.response.getTransports?.() ?? ["internal"],
  },
  clientExtensionResults: c.getClientExtensionResults(),
});

const authResponse = (c) => ({
  id: c.id, rawId: bytesToB64(c.rawId), type: c.type,
  authenticatorAttachment: c.authenticatorAttachment,
  response: {
    clientDataJSON: bytesToB64(c.response.clientDataJSON),
    authenticatorData: bytesToB64(c.response.authenticatorData),
    signature: bytesToB64(c.response.signature),
    userHandle: c.response.userHandle ? bytesToB64(c.response.userHandle) : null,
  },
  clientExtensionResults: c.getClientExtensionResults(),
});

export function isInstalledPwa() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function webauthnSupported() {
  return !!(window.PublicKeyCredential && navigator.credentials);
}

// Safety net: a platform WebAuthn ceremony should resolve within ~60s. If the OS
// sheet never appears (e.g. Android can't surface the passkey), abort so the UI can
// recover instead of hanging on "Verifying…" forever.
const CEREMONY_TIMEOUT_MS = 60_000;

async function ensurePlatformAuthenticator() {
  if (!webauthnSupported()) throw new Error("This device doesn't support biometric lock.");
  try {
    const available = await window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable?.();
    if (available === false) {
      throw new Error("Set up a screen lock (PIN, pattern or fingerprint) on this phone, then try again.");
    }
  } catch (e) {
    if (typeof e?.message === "string" && e.message.startsWith("Set up")) throw e;
    // The availability probe itself failed — let the ceremony proceed and decide.
  }
}

export async function getEnrollStatus() {
  const { data } = await client.get("/webauthn/status");
  return data.enrolled;
}

export async function enrollDevice() {
  await ensurePlatformAuthenticator();
  const { data: options } = await client.post("/webauthn/register/begin");
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CEREMONY_TIMEOUT_MS);
  let credential;
  try {
    credential = await navigator.credentials.create({ publicKey: regOptions(options), signal: controller.signal });
  } catch (e) {
    if (e?.name === "AbortError") throw new Error("Set-up timed out. Please tap to try again.");
    throw e;
  } finally { clearTimeout(timer); }
  if (!credential) throw new Error("Enrolment was cancelled.");
  await client.post("/webauthn/register/complete", regResponse(credential));
}

export async function unlockDevice() {
  const { data: options } = await client.post("/webauthn/authenticate/begin");
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CEREMONY_TIMEOUT_MS);
  let credential;
  try {
    credential = await navigator.credentials.get({ publicKey: authOptions(options), signal: controller.signal });
  } catch (e) {
    if (e?.name === "AbortError") throw new Error("Verification timed out. Please tap to try again.");
    throw e;
  } finally { clearTimeout(timer); }
  if (!credential) throw new Error("Unlock was cancelled.");
  await client.post("/webauthn/authenticate/complete", authResponse(credential));
}
